import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addLocation,
  updateLocation,
  deleteLocation,
  setCurrentLocationIndex,
} from "../../../../store/registrationSlice";
import { toast } from "react-hot-toast";
import PlacesAutocomplete from "../../../../components/form/PlacesAutocomplete";
import GoogleMaps from "../../../../components/atoms/GoogleMaps";
import MultiSelect from "../../../../components/MultiSelect";
import { useCustomEventListener } from "react-custom-events";

const LocationInformation = ({ prevStep, handleSubmit, loading }) => {
  const dispatch = useDispatch();
  const { locations, currentLocationIndex, userInfo, companyInfo } =
    useSelector((state) => ({
      locations: state.registration.locations,
      currentLocationIndex: state.registration.currentLocationIndex,
      userInfo: state.registration.userInfo,
      companyInfo: state.registration.companyInfo,
    }));
  const isEnterprise = useSelector(
    (state) => state.registration.companyInfo.isEnterprise
  );

  const categoriesList = [
    { label: "Food & Beverage", value: "Food & Beverage" },
    { label: "Hospitality", value: "Hospitality" },
    { label: "Maintenance", value: "Maintenance" },
    { label: "Fitness", value: "Fitness" },
    { label: "Apartments", value: "Apartments" },
    { label: "Logistics", value: "Logistics" },
    { label: "Retail", value: "Retail" },
    { label: "Other", value: "Other" },
  ];

  const [formData, setFormData] = useState({
    businessAddress: "",
    businessName: "",
    lat: null,
    lng: null,
    categories: [],
    description: "",
    contactNumber: "",
  });

  const [manualAddress, setManualAddress] = useState(false);
  const [addressData, setAddressData] = useState(null);
  const [isAddressDescription, setIsAddressDescription] = useState(false);
  const [errors, setErrors] = useState({});
  const [showLocationForm, setShowLocationForm] = useState(
    locations.length === 0
  );

  useEffect(() => {
    if (locations[currentLocationIndex]) {
      const currentLocation = locations[currentLocationIndex];
      setFormData(currentLocation);
      if (currentLocation.lat && currentLocation.lng) {
        setAddressData({
          lat: currentLocation.lat,
          lng: currentLocation.lng,
          address: currentLocation.businessAddress,
        });
        setIsAddressDescription(true);
      }
    }
  }, [locations, currentLocationIndex]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.businessAddress || !formData.businessAddress.trim()) {
      newErrors.businessAddress = "Business address is required";
    }

    if (!formData.businessName || !formData.businessName.trim()) {
      newErrors.businessName = "Business name is required";
    }

    if (!formData.contactNumber || !formData.contactNumber.trim()) {
      newErrors.contactNumber = "Contact number is required";
    }

    if (!formData.categories || formData.categories.length === 0) {
      newErrors.categories = "Please select at least one category";
    }

    if (!formData.description || !formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      businessAddress: "",
      businessName: "",
      lat: null,
      lng: null,
      categories: [],
      description: "",
      contactNumber: "",
    });
    setAddressData(null);
    setIsAddressDescription(false);
    setManualAddress(false);
    setErrors({});
  };

  const handleAddLocation = () => {
    setIsEditMode(false);
    dispatch(setCurrentLocationIndex(-1));
    setShowLocationForm(true);
    resetForm();
  };

  const [isEditMode, setIsEditMode] = useState(false);

  const handleFormSubmit = async () => {
    if (validateForm()) {
      if (isEditMode) {
        // Update existing location
        dispatch(
          updateLocation({ index: currentLocationIndex, data: formData })
        );
        toast.success("Location updated successfully");
        setIsEditMode(false);
      } else {
        // Add new location
        dispatch(addLocation(formData));
        toast.success("Location added successfully");
      }

      if (isEnterprise) {
        await new Promise((resolve) => setTimeout(resolve, 0));
        resetForm();
        setShowLocationForm(false);
      } else {
        submitRegistration();
      }
    } else {
      toast.error("Please fill all required fields correctly");
    }
  };

  const submitRegistration = () => {
    // Format locations data
    const formattedLocations = locations.map((location) => ({
      name: location.businessName,
      category_id: location.categories.map((cat) => {
        // Map category names to IDs
        const categoryMap = {
          "Food & Beverage": 1,
          Hospitality: 2,
          Maintenance: 3,
          Fitness: 4,
          Apartments: 5,
          Logistics: 6,
          Retail: 7,
          Other: 8,
        };
        return categoryMap[cat] || 0;
      }),
      description: location.description,
      business_phone: location.contactNumber,
      address: {
        name: location.businessName,
        address: location.businessAddress,
        city: location.city || "Duluth",
        state: location.state || "GA",
        zip: location.zip || "30096",
        lat: location.lat,
        lng: location.lng,
      },
    }));

    // Construct the final payload
    const payload = {
      location: formattedLocations,
      company: {
        name: companyInfo.companyName,
        how_did_you_hear: companyInfo.hearAbout,
        is_enterprise: companyInfo.isEnterprise,
      },
      user: {
        first_name: userInfo.firstName,
        last_name: userInfo.lastName,
        email: userInfo.email,
        phone: userInfo.phone,
        password: userInfo.password,
      },
      image: companyInfo.imageData || {}, // Use imageData (API response) for image
    };

    // Only proceed if we have at least one location
    if (formattedLocations.length > 0) {
      // Pass the payload directly to the parent's handleSubmit
      if (typeof handleSubmit === "function") {
        handleSubmit(payload);
      } else {
        console.error("handleSubmit is not a function");
      }
    } else {
      toast.error("Please add at least one location");
    }
  };

  const handleDeleteLocation = (index) => {
    dispatch(deleteLocation(index));
    if (index === currentLocationIndex) {
      resetForm();
    }
    if (currentLocationIndex >= locations.length - 1) {
      dispatch(setCurrentLocationIndex(Math.max(0, locations.length - 2)));
    }
  };

  const handleEditLocation = (index) => {
    setIsEditMode(true);
    dispatch(setCurrentLocationIndex(index));
    const location = locations[index];
    setFormData(location);
    setAddressData({
      address: location.businessAddress,
      lat: location.lat,
      lng: location.lng,
    });
    setIsAddressDescription(true);
    setShowLocationForm(true);
  };

  const handleInput = (place) => {
    if (place) {
      setFormData((prev) => ({
        ...prev,
        businessAddress: place.formatted_address || place.name,
        businessName: !manualAddress ? place.name : "",
        lat: place.geometry?.location?.lat() || null,
        lng: place.geometry?.location?.lng() || null,
      }));

      setAddressData({
        address: place.formatted_address || place.name,
        lat: place.geometry?.location?.lat() || null,
        lng: place.geometry?.location?.lng() || null,
      });

      setIsAddressDescription(!!place.formatted_address);
    }
  };

  useCustomEventListener("placeSelected", (data) => {
    const Name = data.business_name || data.terms[0]?.value;
    const cityD = data.terms[data.terms.length - 3]?.value;
    const State = data.terms[data.terms.length - 2]?.value;
    const ZipCode = data.zipCode ? data.zipCode : "00000";
    const address = {
      name: Name,
      address: data.description,
      city: cityD,
      state: State,
      zip: ZipCode,
      lat: data.lat,
      lng: data.lng,
      website: data.website,
      categories: data.categories,
      business_number: data.business_number,
    };
    setAddressData(address);
    setIsAddressDescription(true);
    localStorage.removeItem("registerStep2");
    setFieldValue("businessAddress", data.description);
    if (data.business_name) {
      setFieldValue("businessName", data.business_name);
    }
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const setFieldValue = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getErrorMessage = (field) => {
    return errors[field] || "";
  };

  const setMapSelectAddress = (address) => {
    setAddressData(address);
    setFieldValue("businessAddress", address?.address);
    setFieldValue("lat", address?.lat);
    setFieldValue("lng", address?.lng);
  };

  return (
    <div className="bg-white p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        Location Information
      </h2>
      <div className="space-y-6">
        {/* Location List */}
        {isEnterprise && locations.length > 0 && (
          <div className="border-b border-gray-200 pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Added Locations
            </h3>
            <div className="space-y-3">
              {locations.map((location, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between bg-gray-50 p-4 rounded-lg"
                >
                  <div>
                    <h4 className="font-medium">{location.businessName}</h4>
                    <p className="text-sm text-gray-500">
                      {location.businessAddress}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditLocation(index)}
                      className="text-primary-600 hover:text-primary-700"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteLocation(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Location Form */}
        {showLocationForm ? (
          <div className="space-y-6">
            <div>
              <div>
                <PlacesAutocomplete
                  label={"Location Address"}
                  pValue={formData.businessAddress}
                  isAddressSearch={manualAddress}
                  setAddressCheck={handleInput}
                  className="text-base"
                />
                {getErrorMessage("businessAddress") && (
                  <p
                    className={`mt-1 text-xs text-red-500 ${
                      !manualAddress && !addressData?.address && "mb-2"
                    }`}
                  >
                    {getErrorMessage("businessAddress")}
                  </p>
                )}
              </div>

              {!manualAddress &&
                !addressData?.address &&
                !isAddressDescription && (
                  <button
                    onClick={() => {
                      setFieldValue("businessName", "");
                      setManualAddress(true);
                    }}
                    className="text-xs text-gray-500 mt-2 text-base"
                  >
                    Not on Google?{" "}
                    <span className="text-gray-800 underline cursor-pointer text-xs md:text-sm">
                      Enter Business Address manually
                    </span>
                  </button>
                )}
            </div>

            {manualAddress && (
              <div className="mt-4">
                <GoogleMaps
                  location={{
                    address: addressData?.address,
                    lat: addressData?.lat,
                    lng: addressData?.lng,
                  }}
                  onClickAction={setMapSelectAddress}
                />
              </div>
            )}

            {((addressData?.address && isAddressDescription) ||
              manualAddress) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location Name
                </label>
                <input
                  type="text"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md ${
                    errors.businessName
                      ? "border-red-500"
                      : "border-gray-300 focus:border-primary-500"
                  } focus:outline-none focus:ring-1 focus:ring-primary-500`}
                  placeholder="Enter business name"
                />
                {errors.businessName && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.businessName}
                  </p>
                )}
              </div>
            )}

            {/* Categories */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categories
              </label>
              <div className="border border-warmgray-300 rounded-md">
                <MultiSelect
                  name="categories"
                  value={
                    formData.categories?.map((cat) => ({
                      value: cat,
                      label: cat,
                    })) || []
                  }
                  onChange={(selectedOptions) => {
                    setFieldValue(
                      "categories",
                      selectedOptions
                        ? selectedOptions.map((option) => option.value)
                        : []
                    );
                  }}
                  options={categoriesList}
                  isMulti={true}
                  placeholder="Select categories"
                  className={errors.categories ? "is-invalid" : ""}
                />
              </div>
              {errors.categories && (
                <p className="mt-1 text-sm text-red-500">{errors.categories}</p>
              )}
            </div>

            {/* Contact Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Number
              </label>
              <input
                type="tel"
                name="contactNumber"
                value={formData.contactNumber}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md ${
                  errors.contactNumber
                    ? "border-red-500"
                    : "border-gray-300 focus:border-primary-500"
                } focus:outline-none focus:ring-1 focus:ring-primary-500`}
                placeholder="Enter contact number"
              />
              {errors.contactNumber && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.contactNumber}
                </p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className={`w-full px-3 py-2 border rounded-md ${
                  errors.description
                    ? "border-red-500"
                    : "border-gray-300 focus:border-primary-500"
                } focus:outline-none focus:ring-1 focus:ring-primary-500`}
                placeholder="Enter business description"
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.description}
                </p>
              )}
            </div>
          </div>
        ) : null}

        {/* Navigation Buttons */}
        <div className="mt-8 flex justify-between items-center">
          <button
            onClick={prevStep}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Previous
          </button>
          <div className="flex space-x-3">
            {isEnterprise && !showLocationForm && (
              <button
                onClick={handleAddLocation}
                className="px-6 py-2 text-sm font-medium text-primary-600 border border-primary-600 rounded-md hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Add Another Location
              </button>
            )}
            {(showLocationForm || !isEnterprise) && (
              <button
                onClick={handleFormSubmit}
                className="px-6 py-2 text-sm font-medium text-white bg-primary-500 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                {isEnterprise ? "Save Location" : "Submit"}
              </button>
            )}
            {!showLocationForm && isEnterprise && locations.length > 0 && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  submitRegistration();
                }}
                disabled={loading}
                className={`px-6 py-2 text-sm font-medium text-white ${
                  loading
                    ? "bg-primary-400 cursor-not-allowed"
                    : "bg-primary-500 hover:bg-primary-700"
                } rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500`}
              >
                {loading ? "Processing..." : "Continue"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationInformation;
